<template>
  <div id="project-details-edit" class="professional-popup">
    <v-card class="professional-document-card" elevation="24">
      <!-- Professional Header -->
      <div class="professional-header">
        <div class="header-content">
          <div class="header-icon">
            <v-icon size="28" color="white">mdi-calendar-edit</v-icon>
          </div>
          <div class="header-text">
            <h2 class="header-title">{{ pageTitle }}</h2>
            <p class="header-subtitle">Update project details in your award application</p>
          </div>
        </div>
        <v-btn
          icon
          large
          color="white"
          @click="closePrompt"
          class="close-btn"
        >
          <v-icon size="24">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Professional Content Area -->
      <div class="professional-content overflow-y-auto">
        <v-container class="pa-6">
          <div class="d-flex justify-content-center mb-3" v-if="LoadingFlag">
            <b-spinner
              variant="primary"
              type="grow"
              label="Loading..."
            ></b-spinner>
          </div>

          <!-- Form Section -->
          <v-form
            ref="form1"
            v-model="valid1"
            lazy-validation
            v-on:submit.prevent="confirmAlert"
          >
            <!-- Selection Form Card -->
            <v-card class="upload-form-card" elevation="8">
              <v-card-text class="pa-6">
                <div class="form-section-title mb-4">
                  <v-icon color="primary" class="mr-2">mdi-form-select</v-icon>
                  <span>Event Selection</span>
                </div>

                <v-row wrap class="professional-form-row">
                  <!-- Month Selection -->
                  <v-col cols="12" sm="6" lg="4" md="6">
                    <div class="form-field">
                      <label class="field-label">
                        <span class="required-asterisk">*</span>
                        Month Name
                      </label>
                      <v-autocomplete
                        :reduce="(option) => option.value"
                        :items="MonthOptions"
                        :rules="MonthRules"
                        v-model="Month"
                        dense
                        clearable
                        required
                        outlined
                        placeholder="Select month"
                        hide-details="auto"
                        class="professional-select"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" small>mdi-calendar-month</v-icon>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-col>

                  <!-- Event Selection -->
                  <v-col cols="12" sm="6" lg="4" md="6">
                    <div class="form-field">
                      <label class="field-label">
                        <span class="required-asterisk">*</span>
                        Event Name
                      </label>
                      <v-autocomplete
                        :reduce="(option) => option.value"
                        :items="EventOptions"
                        :rules="EventRules"
                        v-model="Event"
                        dense
                        clearable
                        required
                        outlined
                        placeholder="Select event"
                        hide-details="auto"
                        class="professional-select"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" small>mdi-calendar-star</v-icon>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Event Details Card -->
            <v-card v-if="eventData != ''" class="mt-6 event-details-card" elevation="8">
              <v-card-text class="pa-6">
                <div class="form-section-title mb-4">
                  <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                  <span>Event Details</span>
                </div>

                <v-row>
                  <v-col cols="12" sm="6" lg="4" md="6">
                    <div class="detail-field">
                      <h6 class="detail-label">Event Date</h6>
                      <h5 class="detail-value">{{ eventData.DateFromTxt }}</h5>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" lg="4" md="6">
                    <div class="detail-field">
                      <h6 class="detail-label">Portfolio Type</h6>
                      <h5 class="detail-value">{{ eventData.PortfolioName }}</h5>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" lg="4" md="6">
                    <div class="detail-field">
                      <h6 class="detail-label">Event Name</h6>
                      <h5 class="detail-value">{{ eventData.JciEventName }}</h5>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" lg="4" md="6">
                    <div class="detail-field">
                      <h6 class="detail-label">Event Image</h6>
                      <div class="image-gallery">
                        <viewer rebuild :images="eventData.WebsiteImages" class="image-viewer">
                          <img
                            v-for="(src, index) in eventData.WebsiteImages"
                            :key="index"
                            :src="src"
                            class="event-image"
                            alt="Event image"
                          />
                        </viewer>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="12" lg="8" md="12">
                    <div class="detail-field">
                      <h6 class="detail-label">Event Description</h6>
                      <div class="description-content">
                        <p class="detail-description">{{ eventData.JciEventDescription }}</p>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Action Button Section -->
            <div class="action-button-container mt-6 text-center">
              <v-btn
                :disabled="eventData == ''"
                @click.prevent="addEvent"
                :loading="SubmitFlag"
                elevation="4"
                rounded
                x-large
                color="warning"
                class="px-8 action-button"
              >
                <v-icon left>mdi-content-save-edit</v-icon>
                <span class="font-weight-medium">Update Event</span>
              </v-btn>
            </div>
          </v-form>
        </v-container>
      </div>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    addRecordPrompt: {
      required: true,
      type: Boolean,
    },
    pageTitle: {
      required: true,
    },
    recordData: {
      required: true,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SubmitFlag: false,
      SearchFlag: false,

      UploadFlag: false,
      ResultFlag: false,

      addButtonFlag: true,

      eventData: "",
      Project: {},

      MonthRules: [(v) => !!v || "Month Name is required"],
      Month: "",
      MonthOptions: [],
      MonthOptionsLoading: false,

      PortfolioRules: [(v) => !!v || "Portfolio is required"],
      Portfolio: "",
      PortfolioOptions: [],
      PortfolioOptionsLoading: false,

      EventRules: [(v) => !!v || "Event Name is required"],
      Event: "",
      EventOptions: [],
      EventOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;
      }
    },
    MonthOptions: function () {
      console.log("watch MonthOptions");
      this.MonthOptionsLoading = false;
    },
    PortfolioOptions: function () {
      console.log("watch PortfolioOptions");
      this.PortfolioOptionsLoading = false;
    },
    EventOptions: function () {
      console.log("watch EventOptions");
      this.EventOptionsLoading = false;
    },
    Month: function () {
      console.log("Portfolio called");
      this.Portfolio = "";
      this.Event = "";
      this.eventData = "";
      this.getEventOptions();
      // this.getPortfolioOptions();
    },
    // Portfolio: function () {
    //   console.log("Portfolio called");
    //   this.Event = "";
    //   this.eventData = "";
    //   this.getEventOptions();
    // },
    Event: function () {
      console.log("Event called");
      this.eventData = "";
      this.searchForm();
    },
  },
  mounted() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;

      var tr = this.recordData;
      console.log({ tr });

      this.getMonthOptions();
      this.LoadingFlag = false;
    },
    closePrompt() {
      console.log("closePrompt called");
      var tr = this.recordData;
      console.log({ tr });

      var id = tr.SNo;
      console.log({ id });

      this.Project = {
        Index: id,
        EventId: "",
        EventName: "",
        EventDate: "",
        PortfolioId: "",
        PortfolioName: "",
        EventDescription: "",
        EventPhoto: "",
      };
      this.$emit("hideAddRecordPrompt", this.Project);
    },
    getMonthOptions() {
      console.log("getMonthOptions called");
      this.MonthOptionsLoading = true;
      var selectbox1_source = "Month";
      var selectbox1_destination = "MonthOptions";
      var selectbox1_url = "api/jci-month/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getPortfolioOptions() {
      console.log("getPortfolioOptions called");
      this.PortfolioOptionsLoading = true;
      var selectbox1_source = "Portfolio";
      var selectbox1_destination = "PortfolioOptions";
      var selectbox1_url = "api/portfolio/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getEventOptions() {
      console.log("getEventOptions called");
      this.EventOptionsLoading = true;
      var selectbox1_source = "Event";
      var selectbox1_destination = "EventOptions";
      var selectbox1_url = "api/jci-events/options";

      var Month = this.Month;
      console.log({ Month });

      if (Month > 0) {
        var selectbox1_conditions_array = {
          UserInterface: 2,
          Month: Month,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    searchForm() {
      console.log("searchForm called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        this.tableData1 = [];
        this.selected1 = [];
        this.SearchFlag = true;
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/show";
        var upload = {
          UserInterface: 1,
          EventCode: this.Event,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SearchFlag = false;
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.eventData = records;

              thisIns.toast("success", output);
            } else {
              console.log("error=" + output);
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Kindy fill required fields and then submit";
        this.toast("error", message);
      }
    },
    addEvent() {
      console.log("addEvent called");

      var tr = this.recordData;
      console.log({ tr });

      var id = tr.SNo;
      console.log({ id });

      var eventData = this.eventData;
      console.log({ eventData });

      if (eventData != "") {
        this.Project = {
          Index: id,
          EventId: eventData.JciEventId,
          EventName: eventData.JciEventName,
          EventDate: eventData.DateFrom1,
          PortfolioId: eventData.PortfolioId,
          PortfolioName: eventData.PortfolioName,
          EventDescription: eventData.JciEventDescription,
          EventPhoto: eventData.WebsiteImages,
        };
        this.$emit("hideAddRecordPrompt", this.Project);
        this.eventData = "";
        this.Project = {};
      }
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        // this.toast("error", "Kindly fill the required fields", true);
      }
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>
    <style lang="scss">
.professional-popup {
  .professional-document-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .professional-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #1976d2, #0d47a1);
    padding: 20px 24px;
    color: white;

    .header-content {
      display: flex;
      align-items: center;

      .header-icon {
        background-color: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
      }

      .header-text {
        .header-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
          letter-spacing: 0.5px;
        }

        .header-subtitle {
          margin: 4px 0 0;
          font-size: 14px;
          opacity: 0.8;
        }
      }
    }

    .close-btn {
      background-color: rgba(255, 255, 255, 0.15);
      margin-left: 8px;
      transition: background-color 0.3s;

      &:hover {
        background-color: rgba(255, 255, 255, 0.25);
      }
    }
  }

  .professional-content {
    max-height: 600px;
    overflow-y: auto;
    background-color: #f9fafb;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #999;
    }
  }

  .upload-form-card, .event-details-card {
    border-radius: 8px;
    background-color: white;
    transition: transform 0.3s, box-shadow 0.3s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
    }
  }

  .form-section-title {
    font-size: 16px;
    font-weight: 600;
    color: #37474f;
    display: flex;
    align-items: center;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 12px;
  }

  .professional-form-row {
    margin-top: 8px;
  }

  .form-field {
    margin-bottom: 16px;

    .field-label {
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 500;
      color: #546e7a;

      .required-asterisk {
        color: #f44336;
        margin-right: 4px;
      }
    }

    .professional-select {
      ::v-deep .v-input__slot {
        background-color: #f8f9fa !important;
        border: 1px solid #e0e0e0 !important;
        transition: all 0.3s;

        &:hover {
          background-color: #f1f3f4 !important;
          border-color: #bdbdbd !important;
        }

        &:focus-within {
          border-color: #1976d2 !important;
          box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
        }
      }
    }
  }

  .detail-field {
    margin-bottom: 16px;

    .detail-label {
      font-size: 13px;
      font-weight: 500;
      color: #607d8b;
      margin-bottom: 6px;
    }

    .detail-value {
      font-size: 15px;
      font-weight: 500;
      color: #37474f;
      margin: 0;
      background-color: #f5f7f9;
      padding: 8px 12px;
      border-radius: 6px;
      border-left: 3px solid #1976d2;
    }
  }

  .description-content {
    .detail-description {
      font-size: 14px;
      line-height: 1.6;
      color: #455a64;
      margin: 0;
      background-color: #f5f7f9;
      padding: 12px 16px;
      border-radius: 6px;
      border-left: 3px solid #1976d2;
    }
  }

  .image-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .event-image {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    border: 2px solid #e0e0e0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: scale(1.05);
      border-color: #1976d2;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
  }

  .action-button-container {
    margin-top: 24px;
    padding: 16px 0;
  }

  .action-button {
    transition: all 0.3s;
    font-weight: 600 !important;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 8px rgba(255, 152, 0, 0.3) !important;
    min-width: 200px;
    text-transform: none !important;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 12px rgba(255, 152, 0, 0.4) !important;
    }

    &:active {
      transform: translateY(1px);
    }

    .v-icon {
      margin-right: 8px;
    }
  }
}
</style>